import React, { useCallback } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Stack } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';
import { fireTrackAnalytics } from '@atlassian/analytics-bridge';
import { FormattedMessage } from '@atlassian/jira-intl';
import { useFormIndexActions } from '@atlassian/proforma-form-system';
import { Action, ActionSubject, FormUpdateType } from '@atlassian/proforma-utils/analytics';
import { useShowNotice } from '@atlassian/proforma-utils/show-notice';
import { FormRestrictionStatus } from '@atlassian/proforma-form-security';
import { fg } from '@atlassian/jira-feature-gating';
import Link from '@atlassian/jira-common-analytics-v2-wrapped-components/src/link.tsx';
import { FORM_RESTRICTION_URL } from '../../common/constants.tsx';
import { messages } from './messages.tsx';
import { UpdateFormVisibilityResult, type HandleUpdateFormVisibility } from './types.tsx';

export const useHandleUpdateFormVisibility = (): HandleUpdateFormVisibility => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { updateFormVisibility, reportError } = useFormIndexActions();
	const { showNotice } = useShowNotice();

	const handleUpdateFormVisibility = useCallback(
		async (formId: number, toInternal: boolean, formRestrictionStatus?: FormRestrictionStatus) =>
			new Promise<(typeof UpdateFormVisibilityResult)[keyof typeof UpdateFormVisibilityResult]>(
				(resolve) => {
					toInternal
						? showNotice({
								title: <FormattedMessage {...messages.changeFormToInternalModalTitle} />,
								body: <FormattedMessage {...messages.changeFormToInternalModalBody} />,
								closeText: <FormattedMessage {...messages.changeFormToInternalModalClose} />,
								confirmText: <FormattedMessage {...messages.changeFormToInternalModalConfirm} />,
								onConfirmHandler: async () => {
									try {
										await updateFormVisibility(formId, true);

										fireTrackAnalytics(
											createAnalyticsEvent({}),
											`${ActionSubject.FORM} ${Action.UPDATED}`,
											{
												formId,
												formUpdateType: FormUpdateType.SET_INTERNAL,
											},
										);
										resolve(UpdateFormVisibilityResult.SUCCESS);
										// eslint-disable-next-line @typescript-eslint/no-explicit-any
									} catch (error: any) {
										reportError(error, {
											id: 'updateFormVisibility',
											attributes: { formId },
										});
										resolve(UpdateFormVisibilityResult.FAILURE);
									}
								},
								onCloseHandler: () => resolve(UpdateFormVisibilityResult.CANCELLED),
							})
						: showNotice({
								title: <FormattedMessage {...messages.changeFormToExternalModalTitle} />,
								body:
									formRestrictionStatus &&
									formRestrictionStatus === FormRestrictionStatus.RESTRICTED &&
									fg('granular_permissions_on_forms_m1') ? (
										<Stack space="space.300">
											<SectionMessage appearance="warning">
												<FormattedMessage
													{...messages.changeRestrictedFormToExternalWarning}
													values={{
														link: (chunks) => (
															<Link href={FORM_RESTRICTION_URL} target="_blank">
																{chunks}
															</Link>
														),
													}}
												/>
											</SectionMessage>
											<FormattedMessage {...messages.changeFormToExternalModalBody} />
										</Stack>
									) : (
										<FormattedMessage {...messages.changeFormToExternalModalBody} />
									),
								closeText: <FormattedMessage {...messages.changeFormToExternalModalClose} />,
								confirmText: <FormattedMessage {...messages.changeFormToExternalModalConfirm} />,
								onConfirmHandler: async () => {
									try {
										await updateFormVisibility(formId, false);

										fireTrackAnalytics(
											createAnalyticsEvent({}),
											`${ActionSubject.FORM} ${Action.UPDATED}`,
											{
												formId,
												formUpdateType: FormUpdateType.SET_EXTERNAL,
											},
										);
										resolve(UpdateFormVisibilityResult.SUCCESS);
										// eslint-disable-next-line @typescript-eslint/no-explicit-any
									} catch (error: any) {
										reportError(error, {
											id: 'updateFormVisibility',
											attributes: { formId },
										});
										resolve(UpdateFormVisibilityResult.FAILURE);
									}
								},
								onCloseHandler: () => resolve(UpdateFormVisibilityResult.CANCELLED),
							});
				},
			),
		[createAnalyticsEvent, reportError, showNotice, updateFormVisibility],
	);

	return handleUpdateFormVisibility;
};
